// apps/dashboard/src/hooks/use-checkout.ts
// React Query hooks for self-serve checkout (Stripe / Asaas).
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { useMutation, useQuery } from '@tanstack/react-query';

// ── DTOs ──────────────────────────────────────────────────────────────────────
export type CheckoutProvider = 'STRIPE' | 'ASAAS';

export interface CheckoutSessionResponse {
  sessionId: string;
  redirectUrl: string;
}

export interface CheckoutStatusResponse {
  status: 'pending' | 'paid' | 'expired';
  tenantId?: string;
  planCode?: string;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Cria uma Checkout Session (Stripe ou Asaas) para o tenant autenticado.
 * Ao receber a URL de redirect, faz a navegação automaticamente.
 */
export function useCreateCheckoutSession() {
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async ({
      planCode,
      provider,
    }: { planCode: string; provider: CheckoutProvider }) => {
      const successUrl = `${window.location.origin}/checkout/success`;
      const cancelUrl = `${window.location.origin}/checkout/cancel`;

      return api.post<CheckoutSessionResponse>('/billing/checkout/session', {
        planCode,
        provider,
        tenantId: user?.tenantId,
        successUrl,
        cancelUrl,
        email: user?.email,
      });
    },
    onSuccess: (data) => {
      window.location.href = data.redirectUrl;
    },
  });
}

/**
 * Verifica o status de uma checkout session.
 * Faz polling a cada 2 segundos até status === 'paid' ou 'expired'.
 */
export function useCheckoutSession(sessionId: string | null, provider: string) {
  return useQuery({
    queryKey: ['billing', 'checkout', 'session', sessionId, provider],
    queryFn: () =>
      api.get<CheckoutStatusResponse>(
        `/billing/checkout/session/${sessionId}?provider=${provider}`,
      ),
    enabled: !!sessionId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'paid' || status === 'expired') return false;
      return 2000;
    },
  });
}

/**
 * Abre o Stripe Billing Portal para o tenant autenticado.
 * Redireciona para a URL retornada pelo portal.
 */
export function useCustomerPortal() {
  return useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/billing/customer-portal', {
        returnUrl: `${window.location.origin}/billing`,
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}
